import { Button } from "@d12labs/ui/button";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Toolbar } from "@d12labs/ui/toolbar";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Download, Receipt } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { harnessLabel } from "../aggregate";
import { DailyChart, TopRepos } from "../components/daily-chart";
import { downloadText } from "../components/download";
import { SessionsTable } from "../components/sessions-table";
import { dayLabel, SpendHero, StatStrip } from "../components/spend-summary";
import {
	RepoFilter,
	Segmented,
	SessionSearch,
	ToolbarDivider,
} from "../components/toolbar-controls";
import { useLedgerGaps, useLedgerSessions } from "../hooks";
import {
	buildOverview,
	cleanLedgerSearch,
	DEFAULT_SORT,
	defaultDir,
	harnessOptions,
	type LedgerSearch,
	parseLedgerSearch,
	RANGE_OPTIONS,
	type RangeKey,
	repoList,
	toCsv,
} from "../overview";
import { SESSIONS_ANCHOR } from "../sidebar";

const RANGE_LABEL: Record<RangeKey, string> = {
	"7d": "7d",
	"30d": "30d",
	"90d": "90d",
	all: "All",
};

export function LedgerPage() {
	const rawSearch = useSearch({ strict: false }) as Record<string, unknown>;
	const search = useMemo(() => parseLedgerSearch(rawSearch), [rawSearch]);
	const navigate = useNavigate();
	const sessionsQuery = useLedgerSessions();
	const gapsQuery = useLedgerGaps();
	const sessions = sessionsQuery.data ?? [];
	const gaps = gapsQuery.data ?? [];
	const [repoOpen, setRepoOpen] = useState(false);
	const searchRef = useRef<HTMLInputElement>(null);

	const update = useCallback(
		(patch: Partial<LedgerSearch>) =>
			navigate({
				to: "/ledger",
				// Defaults drop out of the URL so the plain view stays /ledger.
				search: (prev: LedgerSearch) => cleanLedgerSearch({ ...prev, ...patch }),
				replace: true,
			}),
		[navigate],
	);
	// A filter change returns the table to page one.
	const setFilter = useCallback(
		(patch: Partial<LedgerSearch>) => update({ ...patch, page: undefined }),
		[update],
	);
	const setQuery = useCallback((q: string) => setFilter({ q: q || undefined }), [setFilter]);

	const overview = useMemo(() => buildOverview(sessions, search), [sessions, search]);
	const harnesses = useMemo(() => harnessOptions(sessions), [sessions]);
	const repos = repoList(search);
	const setRepos = (next: string[]) =>
		setFilter({ repo: next.length ? next.join(",") : undefined });

	const exportCsv = () =>
		downloadText(
			"ledger-sessions.csv",
			toCsv(
				["id", "started", "title", "repo", "harness", "basis", "host", "prompts", "cost_usd"],
				overview.visible.map((s) => [
					s.id,
					s.first,
					s.title,
					s.repo,
					s.harness,
					s.costBasis,
					s.host,
					Math.round(s.userPrompts),
					s.cost.toFixed(4),
				]),
			),
		);

	const subtitle = sessionsQuery.isLoading
		? "Loading sessions…"
		: sessions.length
			? [
					`${overview.visible.length.toLocaleString("en-US")} sessions`,
					overview.firstDay
						? `${dayLabel(overview.firstDay)} – ${dayLabel(overview.lastDay)}`
						: null,
					"source: Postgres",
				]
					.filter(Boolean)
					.join(" · ")
			: "No sessions imported yet";

	const sort = search.sort ?? DEFAULT_SORT;

	return (
		<>
			<PageHeader
				eyebrow="Ledger"
				title="Spend overview"
				subtitle={subtitle}
				actions={
					<>
						{gaps.length ? (
							<Link
								to="/ledger/gaps"
								className="inline-flex h-8 items-center gap-1.5 rounded-md border border-warning/30 bg-warning/10 px-2.5 text-body-sm font-medium text-warning outline-none transition-colors hover:bg-warning/15 focus-visible:ring-2 focus-visible:ring-ring/50"
							>
								<span aria-hidden className="size-2 rounded-full bg-warning" />
								{gaps.length.toLocaleString("en-US")} gaps excluded
							</Link>
						) : null}
						<Button
							variant="secondary"
							size="sm"
							className="h-8"
							onClick={exportCsv}
							disabled={!overview.visible.length}
						>
							<Download className="size-3.5" />
							Export CSV
						</Button>
					</>
				}
			/>

			{sessions.length > 0 ? (
				<Toolbar
					className="flex-wrap"
					left={
						<>
							<Segmented
								label="Date range"
								value={overview.range}
								onChange={(range) => setFilter({ range })}
								options={RANGE_OPTIONS.map((value) => ({ value, label: RANGE_LABEL[value] }))}
							/>
							<ToolbarDivider />
							<Segmented
								label="Harness"
								value={search.harness ?? "all"}
								onChange={(harness) =>
									setFilter({ harness: harness === "all" ? undefined : harness })
								}
								options={[
									{ value: "all", label: "All" },
									...harnesses.map((h) => ({ value: h, label: harnessLabel(h) })),
								]}
							/>
							<ToolbarDivider />
							<RepoFilter
								options={overview.repoOptions}
								selected={repos}
								onChange={setRepos}
								open={repoOpen}
								onOpenChange={setRepoOpen}
							/>
						</>
					}
					right={<SessionSearch value={search.q ?? ""} onChange={setQuery} inputRef={searchRef} />}
				/>
			) : null}

			<PageBody className="space-y-5 px-6 py-5">
				{sessionsQuery.isError ? (
					<EmptyState title="Could not load the ledger" body="The sessions request failed." />
				) : null}
				{!sessionsQuery.isLoading && !sessionsQuery.isError && sessions.length === 0 ? (
					<EmptyState
						icon={Receipt}
						title="No sessions yet"
						body="A rebuild fills this page from the bucket. Data gaps list the objects that did not become a session."
					/>
				) : null}
				{sessions.length > 0 ? (
					<>
						<SpendHero overview={overview} />
						<StatStrip overview={overview} />
						<div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_280px]">
							<DailyChart overview={overview} />
							<TopRepos
								overview={overview}
								selected={repos}
								onPick={(repo) => setRepos(repos.length === 1 && repos[0] === repo ? [] : [repo])}
								onShowAll={() => setRepoOpen(true)}
							/>
						</div>
						<SessionsTable
							id={SESSIONS_ANCHOR}
							sessions={overview.visible}
							sort={sort}
							dir={search.dir ?? defaultDir(sort)}
							page={search.page ?? 1}
							onChange={update}
						/>
					</>
				) : null}
			</PageBody>
		</>
	);
}
